import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User } from '../types';
import { Menu, X } from 'lucide-react';

interface NavbarProps {
  user: User | null;
  onLogout: () => void;
}

const Navbar = ({ user, onLogout }: NavbarProps) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const wingoRef = useRef<HTMLDivElement>(null);
  const daisyRef = useRef<HTMLDivElement>(null);
  const experienceRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const [wingoOpen, setWingoOpen] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (activeDropdown === 'wingo' && wingoRef.current && !wingoRef.current.contains(target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'daisy' && daisyRef.current && !daisyRef.current.contains(target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'experience' && experienceRef.current && !experienceRef.current.contains(target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'user' && userRef.current && !userRef.current.contains(target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  const handleDropdownClick = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleMobileSectionClick = (section: 'wingo' | 'coach') => {
    if (section === 'wingo') {
      setWingoOpen(!wingoOpen);
      setCoachOpen(false); // Close other section
    } else if (section === 'coach') {
      setCoachOpen(!coachOpen);
      setWingoOpen(false); // Close other section
    }
  };

  const handleNavigation = (path: string) => {
    setActiveDropdown(null);
    navigate(path);
  };

  // Close mobile menu on navigation
  const handleMobileNav = (path: string) => {
    closeAllMobileMenus();
    setActiveDropdown(null);
    navigate(path);
  };

  // 1. Add a function to close all mobile menus and sub-sections
  const closeAllMobileMenus = () => {
    setMobileMenuOpen(false);
    setCoachOpen(false);
    setWingoOpen(false);
  };

  return (
    <nav className="bg-white border-b border-gray-200 shadow-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex">
            <Link to="/" className="flex items-center space-x-2" onClick={closeAllMobileMenus}>
              <img src="/favicon.ico" alt="Daisy" className="w-8 h-8 rounded-full" />
              <div className="flex flex-col items-start">
                <span className="text-2xl font-bold text-wingo-600">WINGO World</span>
                <span className="text-[8px] text-gray-500 ml-1">A 320 Track Club Experience</span>
              </div>
            </Link>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden sm:flex items-center space-x-4">
            <div className="relative" ref={wingoRef}>
              <button
                onClick={() => handleDropdownClick('wingo')}
                className="text-gray-600 hover:text-wingo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
              >
                <span className="inline-flex items-center">
                  <span className="text-[#E6C200] font-bold">W</span>
                  <span>INGO</span>
                </span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {activeDropdown === 'wingo' &&
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                  <div className="py-2">
                    <Link to="/ledger" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      Ledger
                    </Link>
                    <Link to="/raq" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      RAQ
                    </Link>
                    <Link to="/wingonomics" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      Wingonomics
                    </Link>
                    <Link to="/obwi-results" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      <span className="text-wingo-600 font-bold">OB</span>
                      <span className="font-bold">WI</span>
                    </Link>
                    <Link to="/wager" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      Wager
                    </Link>
                  </div>
                </div>
              }
            </div>
            <div className="relative" ref={daisyRef}>
              <button
                onClick={() => handleDropdownClick('daisy')}
                className="text-gray-600 hover:text-wingo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
              >
                D<span className="text-[#00bcd4]">AI</span>SY™
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {activeDropdown === 'daisy' && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                  <div className="py-2">
                    <Link to="/dvot" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      D<span className="text-cyan-600">AI</span>SY™ DVOT Dash
                    </Link>
                    <Link to="/converter" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      Wingo Converter
                    </Link>
                    <Link to="/lanes" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      Stay in Yo Lane
                    </Link>
                    <a href="https://www.daisy320.com/merch" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setActiveDropdown(null)}>
                      Merch
                    </a>
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/319"
              className="text-gray-600 hover:text-wingo-600 px-3 py-2 rounded-md text-sm font-medium"
              onClick={() => setActiveDropdown(null)}
            >
              320 (3/19)
            </Link>
            <Link 
              to="/raq" 
              className="text-gray-600 hover:text-wingo-600 px-3 py-2 rounded-md text-sm font-medium"
              onClick={() => setActiveDropdown(null)}
            >
              RAQ
            </Link>
            {user ? (
              <div className="relative" ref={userRef}>
                <button
                  onClick={() => handleDropdownClick('user')}
                  className="flex items-center space-x-2 bg-wingo-50 px-3 py-1.5 rounded-full hover:bg-wingo-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-900 font-medium">{user.wingo_balance}</span>
                    <span className="inline-flex items-center">
                      <span className="text-[#E6C200] font-bold">W</span>
                      <span>INGO</span>
                    </span>
                  </div>
                  <span className="text-gray-600 mx-2">•</span>
                  <span className="text-gray-600">{user.username}</span>
                  <svg className="w-4 h-4 ml-1 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {activeDropdown === 'user' && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    <button
                      onClick={() => handleNavigation('/wallet')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Wallet
                    </button>
                    <button
                      onClick={onLogout}
                      className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) :
              <>
                <Link to="/initiation" className="bg-wingo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-wingo-700">
                  Initiation
                </Link>
              </>
            }
          </div>

          {/* Hamburger for mobile */}
          <button
            className="sm:hidden p-2 rounded-md text-gray-600 hover:text-wingo-600 focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden bg-white border-t border-gray-200 shadow-md px-4 py-4">
          <div>
            {/* WINGO Section (collapsible) */}
            <button
              className="flex items-center w-full text-lg font-semibold uppercase tracking-wide px-4 py-1.5 focus:outline-none border-b border-gray-100"
              onClick={() => handleMobileSectionClick('wingo')}
              aria-expanded={wingoOpen}
              aria-controls="wingo-section"
            >
              <span className="text-[#E6C200]">W</span>
              <span className="text-black">INGO</span>
              <svg className={`ml-2 w-3 h-3 transition-transform ${wingoOpen ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
            {wingoOpen && (
              <div id="wingo-section" className="divide-y divide-gray-100">
                <button
                  onClick={() => handleMobileNav('/ledger')}
                  className="block w-full text-left px-8 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Ledger
                </button>
                <button
                  onClick={() => handleMobileNav('/wingonomics')}
                  className="block w-full text-left px-8 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Wingonomics
                </button>
                <button
                  onClick={() => handleMobileNav('/obwi-results')}
                  className="block w-full text-left px-8 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  OBWI
                </button>
                <button
                  onClick={() => handleMobileNav('/wager')}
                  className="block w-full text-left px-8 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Wager
                </button>
              </div>
            )}
            {/* Coach DAISY™ Section (collapsible) */}
            <button
              className="flex items-center w-full text-lg font-semibold px-4 py-1.5 focus:outline-none border-b border-gray-100"
              onClick={() => handleMobileSectionClick('coach')}
              aria-expanded={coachOpen}
              aria-controls="coach-daisy-section"
            >
              <span className="text-black">Coach D</span>
              <span className="text-[#00bcd4]">AI</span>
              <span className="text-black">SY™</span>
              <svg className={`ml-2 w-3 h-3 transition-transform ${coachOpen ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
            {coachOpen && (
              <div id="coach-daisy-section" className="divide-y divide-gray-100">
                <button
                  onClick={() => handleMobileNav('/dvot')}
                  className="block w-full text-left px-8 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  D<span className="text-[#00bcd4]">AI</span>SY™ DVOT Dashboard
                </button>
                <button
                  onClick={() => handleMobileNav('/converter')}
                  className="block w-full text-left px-8 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Wingo Converter
                </button>
                <button
                  onClick={() => handleMobileNav('/lanes')}
                  className="block w-full text-left px-8 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Stay in Yo Lane
                </button>
                <a
                  href="https://www.daisy320.com/merch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-left px-8 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Merch
                </a>
              </div>
            )}
            {/* RAQ */}
            <button
              onClick={() => handleMobileNav('/raq')}
              className="flex items-center w-full text-lg font-semibold px-4 py-1.5 focus:outline-none text-gray-900 hover:bg-gray-100 border-b border-gray-100"
            >
              RAQ
            </button>
            {/* 320 (3/19) */}
            <button
              onClick={() => handleMobileNav('/319')}
              className="flex items-center w-full text-lg font-semibold px-4 py-1.5 focus:outline-none text-gray-900 hover:bg-gray-100 border-b border-gray-100"
            >
              320 (3/19)
            </button>
            {/* Wingipedia */}
            <button
              onClick={() => handleMobileNav('/wingipedia')}
              className="flex items-center w-full text-lg font-semibold px-4 py-1.5 focus:outline-none text-gray-900 hover:bg-gray-100 border-b border-gray-100"
            >
              Wingipedia
            </button>
            {/* Initiation - emphasized */}
            <button
              onClick={() => handleMobileNav('/initiation')}
              className="flex items-center justify-center w-full text-lg font-bold px-4 py-3 focus:outline-none bg-wingo-600 text-white rounded-lg hover:bg-wingo-700 border-b-0 mt-2 mb-2 shadow-md"
            >
              Initiation
            </button>
            {/* Social Icons Row - Mobile Only */}
            <div className="flex items-center space-x-4 mt-4 pl-4 sm:hidden">
              <a 
                href="https://www.instagram.com/320trackclub/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center w-5 h-5"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://onlyfans.com/coachdaisy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center w-5 h-5"
              >
                <img src="/OnlyFans_Logo_Icon_Blue.svg" alt="OnlyFans" className="w-5 h-5 object-contain" />
              </a>
              <a 
                href="https://partiful.com/u/NTeBYXiuNTaTLYghDXTR3hJ06233" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center w-5 h-5"
              >
                <img src="/partiful.jpeg" alt="Partiful" className="w-5 h-5 object-contain rounded" />
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 